import { type MetaFunction } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { formatDuration, intervalToDuration } from "date-fns";
import { Icon } from "#app/components/ui/icon.tsx";
import { prisma } from "#app/utils/db.server.ts";
import { getCompanyImgSrc } from "#app/utils/misc.tsx";

export const meta: MetaFunction = () => [
	{ title: "Útsölur á Íslandi" },
	{ name: "description", content: "Útsölur á Íslandi" },
	{
		name: "keywords",
		content:
			"útsala, útsölur, rýmingarsala, rýmingarsölur, afslættir, afsláttur, útsölur í dag, útsölur í gangi núna, útsölur á íslandi",
	},
];

export async function loader() {
	const verifiedCompanySales = await prisma.sale.findMany({
		where: {
			validTo: {
				gte: new Date(),
			},
			company: {
				verified: true,
			},
		},
		include: {
			company: {
				select: {
					name: true,
					website: true,
					logo: true,
					locations: true,
					verified: true,
				},
			},
			categories: {
				select: {
					name: true,
				},
			},
		},
		orderBy: {
			createdAt: "desc",
		},
	});

	const nonVerifiedCompanySales = await prisma.sale.findMany({
		where: {
			validTo: {
				gte: new Date(),
			},
			company: {
				verified: false,
			},
		},
		include: {
			company: {
				select: {
					name: true,
					website: true,
					logo: true,
					locations: true,
					verified: true,
				},
			},
			categories: {
				select: {
					name: true,
				},
			},
		},
		orderBy: {
			createdAt: "desc",
		},
	});
	const sales = [...verifiedCompanySales, ...nonVerifiedCompanySales];
	return {
		sales,
	};
}

export default function Index() {
	const { sales } = useLoaderData<typeof loader>();
	return (
		<main className="grid">
			<section
				className={
					"h-[400px] container flex items-center bg-fixed relative py-16 lg:py-24 xl:py-32 bg-gradient-to-tr from-yellow-500/10  via-red-500/10 to-orange-500/10 overflow-hidden"
				}
			>
				<div>
					<h3
						className={
							"font-bold text-orange-500 text-[13px] uppercase tracking-wider mb-3"
						}
					>
						Ekki missa af útsölunum!
					</h3>
					<h2
						className={
							"font-heading text-yellow-950 text-2xl md:text-3xl lg:text-4xl"
						}
					>
						Allar útsölur á einum stað!
					</h2>
				</div>
			</section>
			<div
				className={
					"container mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
				}
			>
				{sales.map((sale, i) => {
					const duration = intervalToDuration({
						start: new Date(),
						end: new Date(sale.validTo),
					});

					const validTo = formatDuration(duration, {
						format: ["months", "days", "hours"],
						delimiter: " ",
						locale: {
							formatDistance: (token, count, options) => {
								if (token === "xMonths") {
									return `${count}mán`;
								}
								if (token === "xDays") {
									return `${count}${
										count.toString().endsWith("1") && count !== 11 ? "D" : "D"
									}`;
								}
								if (token === "xHours") {
									return `${count}Klst`;
								}
								return "";
							},
						},
					});
					return (
						<div
							className={
								"relative col-span-1 flex flex-col bg-white text-yellow-950 border border-gray-300 rounded hover:shadow-lg transition-shadow duration-150 ease-in-out overflow-hidden"
							}
							key={i}
						>
							<div className={"flex flex-row justify-between gap-2"}>
								<div
									className={
										"flex ml-4 mt-2 flex-row gap-2 flex-wrap items-center"
									}
								>
									{sale.company.logo ? (
										<div className={""}>
											<img
												className="w-full max-w-16 max-h-8 object-cover"
												alt={sale.description ?? "Logo fyrirtækis"}
												src={getCompanyImgSrc(sale.company.logo.id)}
											/>
										</div>
									) : (
										<div className="h-8 w-8 rounded-full bg-gray-200" />
									)}
									<div
										className={
											"font-normal truncate tracking-widest text-sm uppercase text-yellow-950"
										}
									>
										<a
											href={
												sale.company.website
													? sale.company.website.startsWith("http")
														? sale.company.website
														: `https://${sale.company.website}?utm_source=utsolur.is&utm_medium=referral&utm_campaign=company_link&utm_content=${sale.company.name}`
													: "#"
											}
											target={sale.company.website ? "_blank" : undefined}
											className={
												"text-xs flex flex-row items-center gap-1 text-blue-600"
											}
											rel="noreferrer"
										>
											<span>{sale.company.name}</span>
											{sale.company.verified && (
												<Icon
													name={"verified"}
													size={"sm"}
													className={"text-yellow-400"}
												/>
											)}
										</a>
									</div>
								</div>
								<div>
									<div
										className={
											"rounded-tr-[3px] rounded-bl-[3px] px-4 py-2 bg-red-700 text-white font-bold"
										}
									>
										<span>
											{`${sale.discountFrom ? `${sale.discountFrom} - ` : ""}${
												sale.discountTo
											}%`}
										</span>
									</div>
								</div>
							</div>
							<div className={"p-4 space-y-4 overflow-hidden"}>
								<div className={"space-y-2"}>
									<h3
										className={
											"font-bold tracking-widest text-lg uppercase text-yellow-950"
										}
									>
										{sale.title}
									</h3>
									{sale.description && (
										<h4 className={"font-light text-yellow-950"}>
											{sale.description}
										</h4>
									)}
								</div>
								<div className={"flex flex-row flex-wrap gap-2"}>
									{sale.categories.slice(0, 2).map((category) => (
										<div
											className={
												"bg-yellow-400 tracking-widest text-sm py-1 px-2 text-yellow-950 font-bold"
											}
										>
											{category.name}
										</div>
									))}
									{sale.categories.length > 2 && (
										<div
											className={
												"bg-yellow-400 tracking-widest text-sm py-1 px-2 text-yellow-950 font-bold"
											}
										>
											+{sale.categories.length - 2}
										</div>
									)}
								</div>
							</div>
							<div className={"flex flex-row justify-between gap-4 mt-auto"}>
								<div
									className={
										"pb-2 pl-4 uppercase flex flex-1 flex-col justify-end font-semibold text-xs"
									}
								>
									{sale.company.locations.map((location, ind) => (
										<div key={ind}>{location.address}</div>
									))}
								</div>
								<div className={"flex flex-col justify-end"}>
									<div
										className={
											"inline-flex mb-[-1px] mr-[-1px] text-sm border border-gray-200 tracking-widest uppercase px-2 py-1 bg-white rounded-tl-[3px] rounded-br-[3px] border-r-0 border-b-0"
										}
									>
										{validTo} eftir
									</div>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		</main>
	);
}
